import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValueMobile } from '../../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { NormalTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import HeadlessTextArea from '../../../shared/component/headless/textarea/HeadlessTextArea';
import { AlertPosition, AlertType, SelectType, SupportArea, SupportType, TextAlignment, getSupportAreaLabel, getSupportTypeLabel } from '../../../shared/enum/EnumRepository';
import { sanitizeString } from '../../../shared/util/sanitize';
import { useSupport } from '../../../shared/hooks/useApi/useSupport';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import SelectBox from '../../../shared/component/SelectBox';
import { SUPPORT_MESSAGE_MAX_LENGTH } from '../../../shared/constants/limits';
import { useLanguageStore } from '../../../shared/store/languageStore';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { MobileHeader, Title } from '../../../shared/component/modal/SearchModalLayout';
import belowIcon from '../../../asset/icon/below.png';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const SupportModalMobile: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { user } = useAuth();
  const { language } = useLanguageStore();
  const { standardModal, clearModal } = useModalStore();
  const { createSupport } = useSupport();
  const { enqueueAlert, setTextLengthExceededAlert } = useStandardAlertStore();
  const [type, setType] = useState<SupportType | null>(null);
  const [area, setArea] = useState<SupportArea | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (type !== null || area !== null || title.length > 0 || description.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [type, area, title, description, setIsUserInput]);

  if (standardModal == null) return null;

  if (user == null) return null;

  const handleChange = async () => {
    if (!type) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SUPPORT_TYPE_REQUIRED'),
      });
      return;
    }
    if (!area) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SUPPORT_AREA_REQUIRED'),
      });
      return;
    }
    const sanitizedTitle = sanitizeString(title);
    const sanitizedDescription = sanitizeString(description);
    if (!sanitizedTitle || !sanitizedDescription) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SUPPORT_CONTENT_REQUIRED'),
      });
      return;
    }
    const areaLabel = getSupportAreaLabel(area, language);
    const messageBody = sanitizedDescription
      ? `[${areaLabel}] ${sanitizedDescription}`
      : `[${areaLabel}]`;
    await createSupport(user.aui, {
      type,
      title: sanitizedTitle,
      message: messageBody,
    });
  };

  const handleChangeWithLimit = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > SUPPORT_MESSAGE_MAX_LENGTH) {
      setTextLengthExceededAlert(SUPPORT_MESSAGE_MAX_LENGTH);
      return;
    }
    setDescription(e.target.value);
  }

  const handleClose = () => {
    clearModal(400);
  };

  return (
    <SupportModalComp>
      <MobileHeader>
        <Title>{getModalMessage('SUPPORT_MODAL_TITLE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </MobileHeader>
      <SelectBoxContainer>
        <SelectBoxWrapper>
          <SelectBox
            value={type ?? SupportType.OTHER}
            selectType={SelectType.SUPPORT_TYPE}
            handleChange={setType}
            direction={false}
            getDisplayText={(v) => (type ? getSupportTypeLabel(v, language) : getModalMessage('SUPPORT_TYPE_PLACEHOLDER'))}
            getOptionDisplayText={(v) => getSupportTypeLabel(v, language)}
          />
        </SelectBoxWrapper>
        <SelectBoxWrapper>
          <SelectBox
            value={area ?? SupportArea.OTHERS}
            selectType={SelectType.SUPPORT_AREA}
            handleChange={setArea}
            direction={false}
            getDisplayText={(v) => (area ? getSupportAreaLabel(v, language) : getModalMessage('SUPPORT_AREA_LABEL'))}
            getOptionDisplayText={(v) => getSupportAreaLabel(v, language)}
          />
        </SelectBoxWrapper>
      </SelectBoxContainer>
      <HeadlessInput
        type={'text'}
        value={title}
        handleChange={(e) => setTitle(e.target.value)}
        placeholder={getModalMessage('PLACEHOLDER_TITLE')}
        StyledInput={MemberInfoValueMobile}
      />
      <DescriptionWrapper>
        <LimitDiv>
          {description.toString().length} / {SUPPORT_MESSAGE_MAX_LENGTH}
        </LimitDiv>
        <HeadlessTextArea
          alignment={TextAlignment.LEFT}
          content={description}
          placeholder={getModalMessage('PLACEHOLDER_DESCRIPTION')}
          handleChange={handleChangeWithLimit}
          StyledTextArea={NormalTextArea}
          $maxRows={5}
        />
      </DescriptionWrapper>
      <HeadlessBtn
        value={getBtnName('SUBMIT')}
        handleClick={handleChange}
        StyledBtn={SubmitButton}
      />
    </SupportModalComp>
  );
};

const SupportModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 14px;
`

const SelectBoxContainer = styled.div`
  width: 100%;
  display: flex;
  gap: clamp(12px, 1vw, 14px);
`;

const SelectBoxWrapper = styled.article`
  width: clamp(70px, 30vw, 100px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;


const DescriptionWrapper = styled.div`
  position: relative;
  width: 100%;
  max-height: 20vh;
  margin-bottom: 20px;
`;

const LimitDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  z-index: 2;
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default SupportModalMobile;
